import React, {useState, useEffect} from "react"
import authentication from "@irati/auth"

import Flex from "@irati/ui/src/Flex"
import Validation from "@irati/utils/validation"
import {Checkbox} from "@irati/ui/src/FormItem"
import Select from "@irati/ui/src/FormItem/Select"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import styled from "styled-components"
import UsersForBusiness, {userList, UsersForBusinessVariables} from "@irati/identity/apollo/query/UsersForBusiness"
import {useBudgetsList} from "@irati/wallet/hooks/budget"
import FormattedMessage, {injectIntl} from "@irati/utils/lang"
import {InjectIntlProps} from "@irati/utils/lang/injectIntl"
import {Budget, RecognitionAutomation} from "@irati/types"
import {parse} from "@perkbox/flexi-points"
import Spacing from "@irati/ui/src/Spacing"
const BudgetsContainer = styled(Flex)`
  .form-group {
    flex: 1;
  }
`
const BUSINESS_USERS_VARIABLES = (): UsersForBusinessVariables => ({
  includeBusiness: false,
  adminsOnly: true,
  enabled: true,
  businessId: authentication.business().uuid
})
interface UserBudgetSelectorProps extends InjectIntlProps {
  onWalletChange: (tenantId: string, budgetId: string, useUnassigned: boolean) => void
  recognitionAutomation?: RecognitionAutomation
}
const UserBudgetSelector = (props: UserBudgetSelectorProps) => {
  const {intl, onWalletChange, recognitionAutomation} = props
  const [selectedTenant, setSelectedTenant] = useState<string>(
    recognitionAutomation?.walletId ? parse(recognitionAutomation?.walletId).entityId : ""
  )
  const {data: budgetData, error} = useBudgetsList({userId: selectedTenant})
  const [useUnassigned, setUseUnassigned] = useState(recognitionAutomation?.budget?.useUnassigned || false)
  const [selectedBudget, setSelectedBudget] = useState<Budget>()
  const onSetUseUnassigned = () => {
    setUseUnassigned(!useUnassigned)
    onWalletChange(
      selectedTenant,
      selectedBudget?.uuid === budgetData?.budgets.unassigned.uuid ? "" : selectedBudget?.uuid,
      !useUnassigned
    )
  }
  useEffect(() => {
    if (!recognitionAutomation) {
      return
    }
    const budgetId = recognitionAutomation?.budget?.categoryId
    const budgetFound = !budgetId ? budgetData?.budgets.unassigned : budgetData?.budgets.categories.find(({uuid}) => uuid === budgetId)
    if (!budgetFound) {
      return
    }
    setSelectedBudget(budgetFound)
  }, [budgetData, recognitionAutomation])
  useEffect(() => {
    if (!selectedTenant || budgetData?.budgets.categories.length) {
      return
    }
    onBudgetSelect(budgetData?.budgets.unassigned.uuid)
  }, [selectedTenant, budgetData])
  const onBudgetSelect = (budgetId: string) => {
    const budgetFound =
      budgetId === budgetData?.budgets.unassigned.uuid
        ? budgetData?.budgets.unassigned
        : budgetData?.budgets.categories.find(({uuid}) => uuid === budgetId)
    if (!budgetFound) {
      return
    }
    setUseUnassigned(false)
    setSelectedBudget(budgetFound)
    onWalletChange(selectedTenant, budgetId === budgetData?.budgets.unassigned.uuid ? "" : budgetId, false)
  }
  const onSelectedTenant = (tenant: string) => {
    setSelectedTenant(tenant)
    setSelectedBudget()
    setUseUnassigned(false)
    onWalletChange(tenant, "", false)
  }
  return (
    <UsersForBusiness variables={BUSINESS_USERS_VARIABLES()} useError={false}>
      {(apollo) => {
        const {results} = userList(apollo)
        const usersOptions = results.map((user) => ({label: `${user.firstName} ${user.lastName}`, value: user.uuid}))
        const budgets =
          budgetData?.budgets.categories.map(({name, amount, uuid}) => ({
            value: name,
            label: `${name} - ${intl.formatNumber(amount / 100)}`,
            uuid
          })) || []
        const budgetOptions =
          budgetData && !error
            ? [
                {
                  value: budgetData?.budgets.unassigned.name!,
                  uuid: budgetData?.budgets.unassigned.uuid!,
                  label: `${budgetData?.budgets.unassigned.name} - ${intl.formatNumber(budgetData?.budgets.unassigned.amount! / 100)}`
                },
                ...budgets
              ]
            : []
        const isUnassignedSelected = selectedBudget?.uuid === budgetData?.budgets.unassigned.uuid!
        return (
          <>
            <BudgetsContainer alignItems="flex-end" gap={2}>
              <Select
                id="automation_selectUser"
                isClearable={false}
                label="admin.automation.peopleRewardWallet.title"
                value={selectedTenant}
                options={usersOptions}
                validation={Validation.string(intl.formatMessage({id: "platform.defaultValidation.required"}))}
                onChange={onSelectedTenant}
              />
              <Select
                key={selectedTenant}
                id="automation_selectBudget"
                isClearable={false}
                isSearchable={false}
                value={selectedBudget?.name}
                getOptionValue={(budget: Budget) => budget.uuid}
                placeholder="platform.reward.budget.distribute.category.placeholder"
                options={budgetOptions}
                onChange={onBudgetSelect}
                validation={Validation.string(intl.formatMessage({id: "platform.defaultValidation.required"}))}
                isDisabled={!selectedTenant || !budgetOptions.length}
                components={{
                  OptionIcon: () => {
                    return <Icon icon={IconTypes.rewardPoints} marginRight={1} size={24} />
                  },
                  ValueContainer: ({children}) => (
                    <Flex paddingLeft={1} maxWidth="80%">
                      {children}
                    </Flex>
                  ),
                  Placeholder: ({children}) => (
                    <Flex flex="1" alignItems="center">
                      <Icon icon={IconTypes.rewardPoints} marginRight={1} size={24} />
                      {children}
                    </Flex>
                  )
                }}
              />
            </BudgetsContainer>
            {selectedTenant && budgetData?.hasBudgets && !!selectedBudget && !isUnassignedSelected && (
              <Spacing marginTop={2}>
                <Checkbox name="check_unassigned" id="checkUnassigned" checked={useUnassigned} onChange={onSetUseUnassigned}>
                  <FormattedMessage id="admin.automation.budgetSelect.useUnassigned.checkbox" />
                </Checkbox>
              </Spacing>
            )}
          </>
        )
      }}
    </UsersForBusiness>
  )
}

export default injectIntl(UserBudgetSelector)
